import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostBinding,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { takeUntil } from 'rxjs/operators';
import { ControlValueAccessor, FormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { DropdownBaseComponent } from '@components/dropdown-base/dropdown-base.component';
import { DropdownType } from '@components/dropdown-base/dropdown-type';
import { trackById } from '@core/utils/list';
import { isDefined } from '@core/utils/object';

@Component({
    selector: 'ihd-dropdown-wrapper',
    template: '',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class DropdownWrapperComponent
    implements OnInit, OnDestroy, ControlValueAccessor {
    /**
     * If overriding this input make sure to call the onItemsSet
     * @param val
     */
    @Input()
    set items(val: any[]) {
        this._items = val;
        this.onItemsSet();
    }

    get items(): any[] {
        return this._items || [];
    }
    //
    // @Input()
    // dropdownType: DropdownType;

    /**
     * Selected property based on item id. Need to possibly rework this.
     * @param value
     */
    @Input()
    set selected(value: any) {
        this.inputValue = value;
        this.processInput = true;
        this.onItemsSet();
    }
    get selected(): any {
        return this.inputValue;
    }

    @Input()
    disabled: boolean;

    /**
     * Use this when the options are objects and not simple strings. The objects key that needs to be used to display
     * the "selected value" must be passed.
     */
    @Input()
    displayKey: string = 'label';

    /**
     * Use this input to introduce a css class for selected item labels, originally used in 'split dropdown' mode
     */
    @Input()
    selectionClass: string = '';

    /**
     * Use this when the options are objects and not simple strings. This must be used a callback function which will be called
     * to display the selected option if using "displayKey" is not sufficient and you need more custom control.
     * The function must return a string that will be used to display the value
     */
    @Input()
    displayFn: ((value: any) => string) | null = null;

    /**
     * Any thing that uniquely identifies the value in the list.
     */
    @Input()
    itemId: string | number = 'id';

    @Input()
    placeholder?: string;

    @Input()
    popoverWidth?: number;

    @Input()
    maxDisplayValueLength?: number;

    // eslint-disable-next-line @angular-eslint/no-output-native
    @Output()
    public change: EventEmitter<any> = new EventEmitter<any>();

    /**
     * Keeping this tabindex to -1 allows this element to be focused via .focus() but we do not this to take focus
     * rather the base inside is using the tabindex to take the focus
     */
    @Input('tabindex')
    tabindex = 0;

    /**
     * Process the value as id only rather than an object
     * This is useful when there is no initial value or initial value is null. In such case the selected value is returned as an object
     * But if you need to be id only then set this input to true
     */
    @Input()
    valueAsIdOnly?: boolean = false;

    @ViewChild(DropdownBaseComponent, { static: true, read: ElementRef })
    autocompleteElement: ElementRef;

    public dropdownType;
    public trackIdFn = trackById;

    public itemControl: FormControl = new FormControl();
    public destroyed$: Subject<void> = new Subject();
    public selectedValue: any;
    public onChange: any;
    public onTouched: any;
    public _items: any[];
    public inputValue: any;
    public processInput: boolean;

    /**
     * Sometimes the formControlValue from the parent could just be an `id` rather than the entire object
     * In that case, the input is converted to the object for internal processing and converted back to id when returning
     * to parent
     */
    public isInputObject: boolean = true;

    private _selectedIndex: number;

    constructor(public cdr: ChangeDetectorRef) {}

    @HostListener('focus', ['$event.target'])
    onFocus(event) {
        if (this.autocompleteElement) {
            this.autocompleteElement.nativeElement.focus();
        }
    }

    ngOnInit() {
        this.itemControl.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe((value) => {
                if (this.onChange) {
                    if (!this.isInputObject || this.valueAsIdOnly) {
                        this.onChange(value[this.itemId]);
                    } else {
                        this.onChange(value);
                    }
                }

                this.selectedValue = this.isInputObject
                    ? value
                    : value[this.itemId];
            });
    }

    ngOnDestroy() {
        this.destroyed$.next();
    }

    registerOnChange(fn: (_: any) => {}) {
        this.onChange = fn;
    }

    registerOnTouched(fn: any) {
        this.onTouched = fn;
    }

    writeValue(value: any) {
        this.inputValue = value;
        const isDefinedValue: boolean = isDefined(value);
        if (
            isDefinedValue &&
            (this.dropdownType === DropdownType.SINGLE ||
                this.dropdownType === DropdownType.DROPDOWN ||
                this.dropdownType === DropdownType.SPLIT_DROPDOWN) &&
            typeof value !== 'object'
        ) {
            this.processInput = true;
            this.isInputObject = false;
            this.onItemsSet();
        } else if (isDefinedValue) {
            this.processInput = false;
            this.itemControl.setValue(value);
        }
    }

    onItemsSet() {
        // Sometimes the formControlValue from the parent could just be an `id` rather than the entire object
        // In that case, the input is converted to the object by matching it from the items input
        // and while passing it back to the parent when selection changes, it is converted to `id` again
        // so it does not need to process it
        if (this.processInput && this.items && this.items.length) {
            // find the item in the list
            const itemInList: any = this.items.find(
                (item: any) => item[this.itemId] === this.inputValue
            );
            this.writeValue(itemInList);
        }
    }

    /**
     * Dropdown value changed event handler.
     * @param value
     */
    dropdownChange(value: any) {
        this.change.emit(value);
    }
}
